import { ExternalLink } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { EMBED_PROVIDER_LABEL, type EmbedProvider } from "@colab/shared";
import { embedUrlForProvider } from "@/lib/content-embed";

// Phase 7 sub-bucket 8 — port of /app/portal/resources/EmbedDrawer.tsx.
// Renders the partner-side preview iframe for an embed asset. The sandbox
// attribute mirrors legacy (allow-scripts allow-same-origin allow-popups
// allow-forms allow-presentation) so Loom / YouTube / Google docs all work.
// If the provider's X-Frame-Options blocks the embed, the bottom strip lets
// the user open the raw URL in a new tab as a fallback.

export function EmbedDrawer({
  open,
  onOpenChange,
  provider,
  embedUrl,
  title,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  provider: EmbedProvider;
  embedUrl: string;
  title: string;
}) {
  const iframeSrc = embedUrlForProvider(provider, embedUrl);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-3xl p-0">
        <SheetHeader className="border-b px-5 py-3">
          <SheetTitle className="font-display tracking-tight">{title}</SheetTitle>
          <SheetDescription className="text-[11px] uppercase tracking-wider">
            {EMBED_PROVIDER_LABEL[provider]}
          </SheetDescription>
        </SheetHeader>

        <div className="flex h-[calc(100%-72px)] flex-col">
          <iframe
            src={iframeSrc}
            className="aspect-video w-full flex-shrink-0 border-b"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
            referrerPolicy="strict-origin-when-cross-origin"
            title={title}
          />
          <div className="flex items-center justify-between px-5 py-3 text-xs text-muted-foreground">
            <span>
              If the preview won&apos;t load, the provider may block embedding —
              open it in a new tab instead.
            </span>
            <a
              href={embedUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1 font-medium text-foreground hover:bg-muted"
            >
              Open in new tab
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
